import { action }                   from 'mobx';
import { computed }                 from 'mobx';
import { makeObservable }           from 'mobx';
import { observable }               from 'mobx';
import { generateId }               from '@tokamap/core';
import { metersToLngLat }           from '@tokamap/core';
import { DECOR_MODELS_BY_ID }       from '@tokamap/core';
import { DECOR_CATEGORIES_BY_ID }   from '@tokamap/core';
import { isDecorModelMovable }      from '@tokamap/core';
import { DEFAULT_STAIRS_SLOPE_DEG } from '@tokamap/core';
import { MAX_STAIRS_SLOPE_DEG }     from '@tokamap/core';
import { MIN_STAIRS_SLOPE_DEG }     from '@tokamap/core';
import type { DecorItem }           from '@tokamap/core';
import type { DecorModel }          from '@tokamap/core';
import type { LngLat }              from '@tokamap/core';
import type { TokaMapInstance }     from '@tokamap/core';
import type { FlightEnd }           from '@/TokamapEditor/decor/decorGeometry';
import { clampDecorScale }          from '@/TokamapEditor/decor/decorGeometry';

type PathDraft = { itemId: string; points: LngLat[] };

const DUPLICATE_OFFSET_METERS = 2;

export class DecorStore {
	@observable.ref items: DecorItem[] = [];
	@observable selectedId: string | null = null;
	/** Élément en mode modification (double-clic) : seul celui-ci se déplace et montre ses poignées. */
	@observable editingId: string | null = null;
	@observable armedModelId: string | null = null;
	@observable dragging = false;
	@observable pathOfferId: string | null = null;
	@observable.ref flightStart: FlightEnd | null = null;
	/** Pente choisie pour l'escalier en cours de pose ; `null` : celle des marches standard. */
	@observable flightSlopeDeg: number | null = null;
	@observable lastRotationDeg = 0;
	@observable lastScale = 1;
	@observable.ref pathDraft: PathDraft | null = null;
	@observable loadedModels = 0;

	private instance: TokaMapInstance | null = null;
	private resumeModelId: string | null = null;

	constructor() {
		makeObservable(this);
	}

	@computed get selected(): DecorItem | null {
		return this.items.find(item => item.id === this.selectedId) ?? null;
	}

	@computed get editing(): DecorItem | null {
		return this.editingId && this.editingId === this.selectedId ? this.selected : null;
	}

	@computed get armedModel(): DecorModel | null {
		return this.armedModelId ? DECOR_MODELS_BY_ID[this.armedModelId] ?? null : null;
	}

	@computed get pathOffer(): DecorItem | null {
		return this.items.find(item => item.id === this.pathOfferId) ?? null;
	}

	@computed get busy(): boolean {
		return !!this.armedModelId || !!this.pathDraft;
	}

	bind(instance: TokaMapInstance): () => void {
		this.instance = instance;
		this.reset(instance.getDecor());

		const unsubscribe = instance.mapAdapter.on(event => {
			if (event.type === 'decor:changed') this.setItems(event.decor);
			if (event.type === 'decor:model-loaded') this.bumpLoadedModels();
		});

		return () => {
			unsubscribe();
			this.instance = null;
			this.reset([]);
		};
	}

	@action private reset(items: DecorItem[]) {
		this.items = items;
		this.selectedId = null;
		this.editingId = null;
		this.armedModelId = null;
		this.dragging = false;
		this.pathOfferId = null;
		this.resumeModelId = null;
		this.flightStart = null;
		this.pathDraft = null;
	}

	@action private setItems(items: DecorItem[]) {
		this.items = items;
		if (this.selectedId && !items.some(item => item.id === this.selectedId)) this.selectedId = null;
		if (this.editingId && !items.some(item => item.id === this.editingId)) this.editingId = null;
		if (this.pathDraft && !items.some(item => item.id === this.pathDraft!.itemId)) this.pathDraft = null;
	}

	@action private bumpLoadedModels() {
		this.loadedModels += 1;
	}

	@action select(id: string | null) {
		this.selectedId = id;
		if (id) this.pathOfferId = null;
		if (id !== this.editingId) this.editingId = null;
	}

	@action edit(id: string | null) {
		this.selectedId = id ?? this.selectedId;
		this.editingId = id;
	}

	@action arm(modelId: string | null) {
		this.armedModelId = this.armedModelId === modelId ? null : modelId;
		this.pathDraft = null;
		this.resumeModelId = null;
		this.flightStart = null;
		this.flightSlopeDeg = null;
		if (this.armedModelId) {
			this.selectedId = null;
			this.editingId = null;
		}
		if (!this.armedModelId) this.instance?.mapAdapter.setDecorPreview(null);
	}

	@action startDrag(modelId: string) {
		if (this.armedModelId !== modelId) this.arm(modelId);
		this.dragging = true;
	}

	@action endDrag(keepArmed = false) {
		if (!this.dragging) return;
		this.dragging = false;
		if (!keepArmed) this.arm(null);
	}

	@action startFlight(start: FlightEnd) {
		this.flightStart = start;
	}

	@action cancelFlight() {
		this.flightStart = null;
	}

	@action adjustFlightSlope(deltaDeg: number) {
		const slope = Math.round((this.flightSlopeDeg ?? DEFAULT_STAIRS_SLOPE_DEG) + deltaDeg);
		this.flightSlopeDeg = Math.min(MAX_STAIRS_SLOPE_DEG, Math.max(MIN_STAIRS_SLOPE_DEG, slope));
	}

	@action rotateArmed(stepDeg: number) {
		this.lastRotationDeg = (((this.lastRotationDeg + stepDeg) % 360) + 360) % 360;
	}

	@action scaleArmed(factor: number) {
		this.lastScale = clampDecorScale(this.lastScale * factor);
	}

	@action place(position: LngLat, rotationDeg = this.lastRotationDeg, extra: Partial<Pick<DecorItem, 'depth' | 'elevation' | 'scale' | 'slopeDeg'>> = {}): DecorItem | null {
		if (!this.armedModelId) return null;

		const item: DecorItem = {
			id: generateId(),
			model: this.armedModelId,
			position,
			rotationDeg: Math.round(rotationDeg) % 360,
			scale: extra.scale ?? Math.round(this.lastScale * 100) / 100,
			...(extra.depth !== undefined ? { depth: extra.depth } : {}),
			...(extra.elevation !== undefined ? { elevation: extra.elevation } : {}),
			...(extra.slopeDeg !== undefined ? { slopeDeg: extra.slopeDeg } : {}),
		};

		this.instance?.addDecor([item]);
		this.pathOfferId = isDecorModelMovable(DECOR_MODELS_BY_ID[item.model]!) ? item.id : null;

		return item;
	}

	update(id: string | string[], patch: Partial<Omit<DecorItem, 'id'>>, transient = false) {
		if (patch.rotationDeg !== undefined) {
			patch.rotationDeg = ((Math.round(patch.rotationDeg) % 360) + 360) % 360;
			this.rememberRotation(patch.rotationDeg);
		}

		this.instance?.updateDecor(id, patch, transient ? { skipHistory: true, skipSave: true } : undefined);
	}

	@action private rememberRotation(rotationDeg: number) {
		this.lastRotationDeg = rotationDeg;
	}

	@action duplicate(id: string) {
		const source = this.items.find(item => item.id === id);
		if (!source) return;

		const shift = (point: LngLat) => metersToLngLat(point, DUPLICATE_OFFSET_METERS, -DUPLICATE_OFFSET_METERS);
		const copy: DecorItem = {
			...source,
			id: generateId(),
			position: shift(source.position),
			path: source.path?.map(shift),
		};

		this.instance?.addDecor([copy]);
		this.selectedId = copy.id;
	}

	@action remove(id: string) {
		this.instance?.deleteShape(id);
		if (this.selectedId === id) this.selectedId = null;
	}

	@action startPath(id: string) {
		const item = this.items.find(candidate => candidate.id === id);
		if (!item) return;

		this.resumeModelId = this.armedModelId;
		this.armedModelId = null;
		this.pathOfferId = null;
		this.pathDraft = { itemId: id, points: [item.position] };
	}

	@action dismissPathOffer() {
		this.pathOfferId = null;
	}

	@action addPathPoint(point: LngLat) {
		if (this.pathDraft) this.pathDraft = { ...this.pathDraft, points: [...this.pathDraft.points, point] };
	}

	@action finishPath() {
		const draft = this.pathDraft;
		this.pathDraft = null;
		this.resumePlacing();
		if (!draft || draft.points.length < 2) return;

		const item = this.items.find(candidate => candidate.id === draft.itemId);
		if (!item) return;

		this.update(item.id, {
			path: draft.points,
			position: draft.points[0]!,
			speedKmh: item.speedKmh ?? DECOR_CATEGORIES_BY_ID[DECOR_MODELS_BY_ID[item.model]!.category].defaultSpeedKmh,
			pathEnd: item.pathEnd ?? 'restart',
		});
	}

	@action cancelPath() {
		this.pathDraft = null;
		this.resumePlacing();
	}

	@action private resumePlacing() {
		if (this.resumeModelId) this.armedModelId = this.resumeModelId;
		this.resumeModelId = null;
	}

	removePath(id: string) {
		this.update(id, { path: undefined, speedKmh: undefined, pathEnd: undefined });
	}
}

export default new DecorStore();
